import React from "react";
import { GENERATOR_CODES, type GeneratorCode } from "@csk/core";
import {
  DemoWorkbenchCard,
  DemoWorkbenchFact,
  DemoWorkbenchFactGrid,
  DemoWorkbenchPill,
  DemoWorkbenchTable,
} from "@csk/demo-ui";

interface RuleChange {
  readonly lhs: string;
  readonly rhs: string;
  readonly from?: string;
  readonly to?: string;
}

interface ProposalDelta {
  readonly added_bridges?: readonly string[];
  readonly removed_bridges?: readonly string[];
  readonly rule_changes?: readonly RuleChange[];
  readonly footprint_delta?: Readonly<Partial<Record<GeneratorCode, number>>>;
}

interface ProposalPayload {
  readonly proposer?: string;
  readonly content_hash?: string;
  readonly review?: { readonly status?: string };
  readonly delta?: ProposalDelta;
}

export function ProposalPanel(props: {
  proposal: ProposalPayload | null;
}): React.JSX.Element {
  if (!props.proposal) {
    return (
      <DemoWorkbenchCard
        title="No proposal loaded"
        right={<DemoWorkbenchPill>optional</DemoWorkbenchPill>}
      >
        <div className="text-sm text-slate-300">
          Provide <code>public/data/proposal-example.json</code> or your own
          proposal artifact.
        </div>
      </DemoWorkbenchCard>
    );
  }
  const p = props.proposal;
  const d: ProposalDelta = p.delta ?? {};
  const adds: readonly string[] = d.added_bridges ?? [];
  const rems: readonly string[] = d.removed_bridges ?? [];
  const rules: readonly RuleChange[] = d.rule_changes ?? [];
  const fpD: Readonly<Partial<Record<GeneratorCode, number>>> =
    d.footprint_delta ?? {};
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <DemoWorkbenchCard
        title="Proposal"
        right={
          <DemoWorkbenchPill>{p.review?.status ?? "UNKNOWN"}</DemoWorkbenchPill>
        }
      >
        <div className="text-sm text-slate-300">
          Proposer: <span className="text-slate-100">{p.proposer ?? ""}</span>
        </div>
        <div className="text-sm text-slate-300 mt-1">
          Content hash:{" "}
          <span className="text-slate-100">
            {p.content_hash ?? "".slice(0, 12)}…
          </span>
        </div>
        <div className="text-xs text-slate-400 mt-2">
          This is a δ-change request (bridge/matrix/footprint diff) intended for
          review.
        </div>
      </DemoWorkbenchCard>

      <DemoWorkbenchCard title="Footprint delta">
        <DemoWorkbenchFactGrid minColumnWidth={110}>
          {GENERATOR_CODES.map((k) => (
            <DemoWorkbenchFact
              key={k}
              label={k}
              value={String(fpD[k] ?? 0)}
              tone={(fpD[k] ?? 0) === 0 ? "default" : "accent"}
              mono
            />
          ))}
        </DemoWorkbenchFactGrid>
      </DemoWorkbenchCard>

      <DemoWorkbenchCard title="Bridge delta">
        <div className="text-sm text-slate-300">
          Added: {adds.length ? adds.join(", ") : "—"}
        </div>
        <div className="text-sm text-slate-300 mt-1">
          Removed: {rems.length ? rems.join(", ") : "—"}
        </div>
      </DemoWorkbenchCard>

      <DemoWorkbenchCard
        title="Matrix rule changes"
        right={<DemoWorkbenchPill>{rules.length}</DemoWorkbenchPill>}
      >
        <DemoWorkbenchTable
          headers={["lhs", "rhs", "from", "to"]}
          rows={rules.map((r) => [r.lhs, r.rhs, r.from ?? "", r.to ?? ""])}
        />
      </DemoWorkbenchCard>
    </div>
  );
}
